import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import ReplayGreeting from "@/components/ReplayGreeting";
import StrikeLibrary from "@/components/StrikeLibrary";
import {
  playEntryChime,
  markGridInitialized,
  isGridInitialized,
  speakAs,
  unlockAudio,
} from "@/lib/audio";
import {
  ArrowRight,
  Users,
  Building2,
  Shield,
  Zap,
  Volume2,
  MapPin,
  Compass,
  BookOpen,
  Bike,
  Sparkles,
  Radio,
  Waves,
} from "lucide-react";

const LEADER_COCKPIT_URL = "/leader-preview?key=xMLODyAIPhoHU62m1pJnxh130RijAWOX";

export default function Landing() {
  const [gateOpen, setGateOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return !isGridInitialized();
  });

  const enterGrid = () => {
    // Fire Alfred's greeting + a Grid Thrum chime — this click satisfies
    // the browser autoplay policy so all subsequent character speech works.
    unlockAudio();
    playEntryChime();
    speakAs("alfred");
    markGridInitialized();
    setGateOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#060B14] text-white relative overflow-hidden flex flex-col">
      {gateOpen && <AudioGate onEnter={enterGrid} />}
      {/* Ambient backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.10) 0%, transparent 55%), radial-gradient(ellipse at 50% 90%, rgba(96,165,250,0.06) 0%, transparent 60%), linear-gradient(180deg, #060B14 0%, #0A1122 55%, #060B14 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-2 text-[10px] hud-label shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-[#34D399] light-pulse glow-green" />
            Architect Online
          </div>
        </div>
      </header>

      {/* Utility Company Hero — sits above the master switchboard */}
      <div
        className="relative z-10 pt-6 sm:pt-8 px-4 sm:px-6"
        data-testid="landing-utility-hero"
      >
        <div
          className="max-w-4xl mx-auto rounded-sm border px-4 sm:px-8 py-4 sm:py-5 relative overflow-hidden"
          style={{
            borderColor: "rgba(212,175,55,0.4)",
            background:
              "linear-gradient(90deg, rgba(212,175,55,0.1) 0%, rgba(6,11,20,0.7) 60%, rgba(212,175,55,0.06) 100%)",
          }}
        >
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,175,55,0.85), transparent)",
            }}
          />
          <div className="text-[10px] uppercase tracking-[0.34em] text-gold mb-2 flex items-center gap-2">
            <Zap size={11} /> The Last 50 Mile · AI Utility Company
          </div>
          <p
            className="font-display text-sm sm:text-lg text-white leading-snug break-words"
            data-testid="last-50-mile-hero-sentence"
          >
            Welcome to the Last 50 Mile AI Utility Company. Before we show you
            what AI can do, we&apos;d like to show you what it{" "}
            <span className="text-gold italic">feels</span> like.
          </p>
        </div>
      </div>

      {/* Center switchboard */}
      <main className="relative z-10 px-4 sm:px-6 py-10 sm:py-12 text-center flex flex-col items-center">
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <div className="hud-line w-8 sm:w-10" />
          <span className="hud-label">Master Switchboard</span>
          <div className="hud-line w-8 sm:w-10" />
        </div>

        {/* ODG Winged Emblem (large, glow) */}
        <LargeEmblem />

        <h1
          className="mt-8 sm:mt-10 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight"
          data-testid="landing-tagline"
        >
          Choose Your <span className="text-gold italic">Path</span>
        </h1>
        <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-lg px-2">
          Two lanes into the Wingman Alfred AI Utility. Pick the one that fits your mission —
          your AI team is standing by.
        </p>
        <div className="mt-4">
          <ReplayGreeting character="alfred" />
        </div>

        {/* Two lane doors */}
        <div
          className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 max-w-4xl w-full"
          data-testid="landing-lanes"
        >
          <LaneCard
            to="/handshake"
            testId="landing-cta-recruits"
            kicker="Lane 01"
            icon={<Users size={22} />}
            label="For Recruits"
            title="Begin AI Utility Assessment"
            sub="Sign the 7-Hour Handshake and enter Pilot training on the Last 50 Mile Grid."
            accent="#D4AF37"
          />
          <LaneCard
            to="/local"
            testId="landing-cta-businesses"
            kicker="Lane 02"
            icon={<Building2 size={22} />}
            label="For Businesses"
            title="Begin AI Utility Assessment"
            sub="Meet your AI team — a concierge audit with the Service Director."
            accent="#60A5FA"
          />
        </div>
      </main>

      {/* Section · The Ohio Digital Grid */}
      <SectionOhioGrid />

      {/* Section · About the Utility */}
      <SectionAboutUtility />

      {/* Section · Industrial Strike Library (Vance) */}
      <StrikeLibrary />

      {/* Section · Resource Library */}
      <SectionResourceLibrary />

      {/* Insider footer: Leader Cockpit */}
      <footer className="relative z-10 pb-10 pt-6">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex justify-center">
          <Link
            to={LEADER_COCKPIT_URL}
            data-testid="landing-leader-cockpit-link"
            className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-slate-500 hover:text-gold transition-colors"
          >
            <Shield size={11} className="opacity-70 group-hover:opacity-100" />
            <span>Leader Cockpit · Partnership Login</span>
            <ArrowRight size={11} className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </Link>
        </div>
      </footer>
    </div>
  );
}

function LargeEmblem() {
  return (
    <div className="relative" data-testid="landing-emblem">
      {/* Halo glow */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.08) 40%, transparent 70%)",
          filter: "blur(24px)",
          transform: "scale(1.4)",
        }}
      />
      <svg
        width="180"
        height="180"
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="relative sm:w-[220px] sm:h-[220px]"
        aria-label="ODG Winged Emblem"
      >
        {/* Outer ring */}
        <circle cx="100" cy="100" r="94" stroke="#D4AF37" strokeWidth="1.5" opacity="0.6" />
        {/* Inner ring */}
        <circle cx="100" cy="100" r="80" stroke="#D4AF37" strokeWidth="1" opacity="0.35" />
        {/* Cardinal ticks */}
        {[0, 90, 180, 270].map((deg) => (
          <line
            key={deg}
            x1="100"
            y1="6"
            x2="100"
            y2="18"
            stroke="#D4AF37"
            strokeWidth="1.5"
            transform={`rotate(${deg} 100 100)`}
          />
        ))}
        {/* Wing feathers - left */}
        <g stroke="#D4AF37" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.9">
          <path d="M100 100 L30 82" />
          <path d="M100 100 L40 96" />
          <path d="M100 100 L45 110" />
          <path d="M100 100 L52 122" />
        </g>
        {/* Wing feathers - right */}
        <g stroke="#D4AF37" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.9">
          <path d="M100 100 L170 82" />
          <path d="M100 100 L160 96" />
          <path d="M100 100 L155 110" />
          <path d="M100 100 L148 122" />
        </g>
        {/* Directional star */}
        <path d="M100 24 L108 96 L100 88 L92 96 Z" fill="#D4AF37" />
        <path d="M100 176 L108 104 L100 112 L92 104 Z" fill="#D4AF37" opacity="0.35" />
        {/* Center orb */}
        <circle cx="100" cy="100" r="9" fill="#060B14" stroke="#D4AF37" strokeWidth="2" />
        <circle cx="100" cy="100" r="2.5" fill="#D4AF37" />
        {/* Bottom scroll banner */}
        <path d="M60 180 Q100 190 140 180" stroke="#D4AF37" strokeWidth="1" fill="none" opacity="0.5" />
      </svg>
    </div>
  );
}

function LaneCard({ to, testId, kicker, icon, label, title, sub, accent }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative glass-strong rounded-sm border border-white/10 hover:border-gold/60 p-6 sm:p-8 text-left transition-all overflow-hidden min-h-[220px] sm:min-h-0 block active:scale-[0.99]"
      style={{
        boxShadow: "0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ backgroundColor: accent, opacity: 0.6 }}
      />
      <div
        className="absolute -bottom-32 -right-16 w-64 h-64 rounded-full opacity-0 group-hover:opacity-40 transition-opacity"
        style={{ background: `radial-gradient(circle, ${accent}55, transparent 65%)` }}
      />

      <div className="relative flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-sm border flex items-center justify-center transition-colors"
            style={{ borderColor: `${accent}66`, color: accent }}
          >
            {icon}
          </div>
          <div>
            <div className="hud-label text-[9px]">{kicker}</div>
            <div className="text-sm text-slate-300 mt-0.5">{label}</div>
          </div>
        </div>
        <ArrowRight
          size={18}
          className="text-slate-500 group-hover:text-gold group-hover:translate-x-1 transition-all"
        />
      </div>

      <div className="relative mt-8">
        <div className="font-display text-2xl sm:text-3xl text-white font-light leading-tight">
          {title}
        </div>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{sub}</p>
      </div>

      <div className="relative mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-display group-hover:gap-3 transition-all" style={{ color: accent }}>
        Enter Lane <ArrowRight size={14} />
      </div>
    </Link>
  );
}


// ============================================================
// Grid Initialization · Audio Gate
// ============================================================
// Browsers block autoplay of audio + SpeechSynthesis until the user
// interacts. This fullscreen overlay collects that one click and unlocks
// both channels. Shows once per session (see GATE_KEY).
function AudioGate({ onEnter }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      data-testid="audio-gate"
      style={{
        background:
          "radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.16) 0%, rgba(6,11,20,0.98) 55%), #060B14",
      }}
      onClick={onEnter}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onEnter()}
    >
      {/* Ambient scanlines */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      {/* Pulsing rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full border"
            style={{
              width: 220 + i * 110,
              height: 220 + i * 110,
              borderColor: "rgba(212,175,55,0.18)",
              animation: `gate-ring 3.6s ${i * 0.6}s ease-out infinite`,
            }}
          />
        ))}
      </div>

      <div className="relative text-center max-w-lg px-6">
        <div className="mx-auto w-16 h-16 rounded-sm border-2 flex items-center justify-center mb-6"
          style={{
            borderColor: "rgba(212,175,55,0.65)",
            boxShadow: "0 0 40px rgba(212,175,55,0.35)",
          }}
        >
          <Waves size={26} className="text-gold" />
        </div>
        <div className="text-[10px] hud-label text-gold mb-3 tracking-[0.42em]">
          GRID INITIALIZATION
        </div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Enter the <span className="text-gold italic">Grid</span>.
        </h2>
        <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
          Tap once to activate audio. Alfred will greet you the moment you cross the threshold.
        </p>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEnter(); }}
          data-testid="audio-gate-enter"
          className="mt-8 btn-gold px-8 py-4 rounded-sm text-sm font-display uppercase tracking-[0.28em] flex items-center gap-3 min-h-[56px] mx-auto"
          style={{ boxShadow: "0 0 40px rgba(212,175,55,0.3), 0 10px 30px rgba(0,0,0,0.7)" }}
        >
          <Volume2 size={16} /> Enter The Grid
        </button>

        <div className="mt-6 text-[10px] font-mono-aviation text-slate-500 tracking-[0.28em]">
          AUDIO · UNLOCKED ON ENTRY
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Section · The Ohio Digital Grid Map
// ============================================================
const OHIO_NODES = [
  {
    id: "marion",
    label: "Marion",
    role: "Node 01 · Anchor Foyer",
    x: 320, y: 210,
    accent: "#D4AF37",
    notes: "Home base · Wingman Alfred HQ",
  },
  {
    id: "wooster",
    label: "Wooster",
    role: "Node 02 · Bentley Adventure Captain",
    x: 420, y: 175,
    accent: "#34D399",
    notes: "Campground vertical · River Bend",
  },
  {
    id: "delaware",
    label: "Delaware",
    role: "Node 03 · Concierge Rollout",
    x: 340, y: 260,
    accent: "#60A5FA",
    notes: "Local trades corridor · pilot cluster",
  },
];

function SectionOhioGrid() {
  return (
    <section
      className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20"
      data-testid="landing-ohio-grid"
    >
      <div className="flex items-center gap-3 mb-6 justify-center">
        <div className="hud-line w-10" />
        <span className="hud-label text-gold flex items-center gap-2">
          <MapPin size={12} /> The Ohio Digital Grid
        </span>
        <div className="hud-line w-10" />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-center max-w-3xl mx-auto">
        Three <span className="text-gold italic">live nodes</span>. One shared spine.
      </h2>
      <p className="mt-4 text-slate-400 text-base text-center max-w-2xl mx-auto leading-relaxed">
        Every Alfred instance runs on the same twelve rails — Marion writes the
        blueprint, Wooster and Delaware ship it to Main Street.
      </p>

      <div className="mt-10 grid lg:grid-cols-5 gap-6 items-start">
        {/* Map */}
        <div className="lg:col-span-3 min-w-0">
          <div
            className="relative rounded-sm border border-white/10 overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(52,211,153,0.06) 0%, rgba(6,11,20,0.95) 55%, rgba(6,11,20,1) 100%)",
              boxShadow: "0 30px 60px -25px rgba(52,211,153,0.2)",
            }}
          >
            <svg viewBox="0 0 700 400" className="w-full h-auto block" data-testid="landing-ohio-svg">
              <defs>
                <linearGradient id="ohio-stroke" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#34D399" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.6" />
                </linearGradient>
                <linearGradient id="ohio-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(52,211,153,0.14)" />
                  <stop offset="100%" stopColor="rgba(212,175,55,0.06)" />
                </linearGradient>
              </defs>

              {/* Grid backdrop */}
              <g opacity="0.15">
                {[...Array(14)].map((_, i) => (
                  <line key={`v-${i}`} x1={i * 50} y1="0" x2={i * 50} y2="400" stroke="#D4AF37" strokeWidth="0.4" />
                ))}
                {[...Array(9)].map((_, i) => (
                  <line key={`h-${i}`} x1="0" y1={i * 50} x2="700" y2={i * 50} stroke="#D4AF37" strokeWidth="0.4" />
                ))}
              </g>

              {/* Stylized Ohio outline — simplified silhouette */}
              <path
                d="M 200 80 L 210 60 L 240 55 L 285 60 L 340 55 L 400 60 L 465 68 L 505 90 L 515 130 L 505 165 L 495 190 L 505 220 L 515 260 L 500 305 L 460 335 L 400 350 L 340 355 L 275 340 L 220 320 L 195 285 L 200 240 L 195 195 L 200 155 L 200 120 Z"
                fill="url(#ohio-fill)"
                stroke="url(#ohio-stroke)"
                strokeWidth="2"
                data-testid="landing-ohio-outline"
              />

              {/* Node connections — dashed lines showing the utility spine */}
              {OHIO_NODES.map((n, i) => {
                const next = OHIO_NODES[(i + 1) % OHIO_NODES.length];
                return (
                  <line
                    key={`link-${n.id}`}
                    x1={n.x}
                    y1={n.y}
                    x2={next.x}
                    y2={next.y}
                    stroke="#D4AF37"
                    strokeOpacity="0.4"
                    strokeWidth="1"
                    strokeDasharray="4 6"
                  />
                );
              })}

              {/* Nodes */}
              {OHIO_NODES.map((n) => (
                <g key={n.id} data-testid={`landing-ohio-node-${n.id}`}>
                  {/* Pulse */}
                  <circle cx={n.x} cy={n.y} r="10" fill="none" stroke={n.accent} strokeOpacity="0.6">
                    <animate attributeName="r" from="6" to="22" dur="2.4s" repeatCount="indefinite" />
                    <animate attributeName="stroke-opacity" from="0.6" to="0" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  {/* Dot */}
                  <circle cx={n.x} cy={n.y} r="6" fill={n.accent} />
                  <circle cx={n.x} cy={n.y} r="2.5" fill="#060B14" />
                  {/* Label */}
                  <text
                    x={n.x + 12}
                    y={n.y - 8}
                    fill="#ffffff"
                    fontSize="13"
                    fontFamily="Outfit, sans-serif"
                    fontWeight="500"
                  >
                    {n.label}
                  </text>
                  <text
                    x={n.x + 12}
                    y={n.y + 8}
                    fill={n.accent}
                    fontSize="9"
                    fontFamily="Manrope, sans-serif"
                    letterSpacing="2"
                  >
                    {n.role.split(" · ")[0]}
                  </text>
                </g>
              ))}
            </svg>
            <div className="absolute bottom-2 right-3 text-[9px] font-mono-aviation text-slate-500 tracking-[0.28em]">
              OHIO · LIVE MAP
            </div>
          </div>
        </div>

        {/* Node dossier */}
        <div className="lg:col-span-2 min-w-0 space-y-3" data-testid="landing-ohio-list">
          {OHIO_NODES.map((n) => (
            <div
              key={n.id}
              className="glass-strong rounded-sm border p-4"
              style={{ borderColor: `${n.accent}55` }}
              data-testid={`landing-ohio-card-${n.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ background: n.accent }} />
                <div className="text-[10px] uppercase tracking-[0.28em] font-mono-aviation" style={{ color: n.accent }}>
                  {n.role}
                </div>
              </div>
              <div className="font-display text-xl text-white leading-tight">{n.label}, OH</div>
              <div className="text-xs text-slate-400 mt-1 leading-relaxed">{n.notes}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Section · About the Utility
// ============================================================
function SectionAboutUtility() {
  return (
    <section
      className="relative z-10 border-y border-white/5"
      style={{
        background:
          "linear-gradient(180deg, rgba(6,11,20,0) 0%, rgba(52,211,153,0.04) 50%, rgba(6,11,20,0) 100%)",
      }}
      data-testid="landing-about-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-3 min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="hud-line w-8" />
            <span className="hud-label text-emerald-300 flex items-center gap-2">
              <Compass size={12} /> About The Utility
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            Small-town shops finally get an{" "}
            <span className="text-gold italic">AI-team</span> — not another tool.
          </h2>
          <p className="mt-5 text-slate-300 text-base leading-relaxed max-w-2xl">
            The Last-50-Mile AI Utility Company was built on one belief:{" "}
            <span className="text-white">
              the internet already reached Main Street — the intelligence
              didn&apos;t.
            </span>{" "}
            Alfred, Amanda, and Mike are three coordinated AI operators who
            answer the phone, greet walk-ins, close the loop with mechanics,
            and hand off cleanly to humans. One monthly utility bill. Zero
            training required.
          </p>
          <p className="mt-4 text-slate-400 text-base leading-relaxed max-w-2xl">
            We don&apos;t sell software. We wire your shop into a grid — the
            same way Ohio wired every farmhouse in 1935.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg" data-testid="landing-about-stats">
            <AboutStat n="12" label="Rails / Node" />
            <AboutStat n="24/7" label="Answered" />
            <AboutStat n="< 60s" label="Handoff" />
          </div>
        </div>

        <div className="lg:col-span-2 min-w-0 space-y-4">
          <LeaderCard
            name="John Delawder"
            role="Lead Architect · Marion, OH"
            testId="landing-leader-john"
            accent="#D4AF37"
            initials="JD"
            bio="Built the original 7-Hour Handshake. Runs the Marion node personally — every shop that plugs in gets an hour on his calendar."
          />
          <LeaderCard
            name="Parker"
            role="Field Director · Wooster + Delaware"
            testId="landing-leader-parker"
            accent="#34D399"
            initials="PK"
            bio="Runs Wooster & Delaware. Owns the Bentley Adventure Captain node and every mechanic-facing rollout. Shipping is his religion."
          />
        </div>
      </div>
    </section>
  );
}

function AboutStat({ n, label }) {
  return (
    <div className="text-left">
      <div className="font-display text-3xl sm:text-4xl text-gold leading-none">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-aviation mt-1">
        {label}
      </div>
    </div>
  );
}

function LeaderCard({ name, role, bio, initials, accent, testId }) {
  return (
    <div
      className="glass-strong rounded-sm border p-5"
      style={{ borderColor: `${accent}55` }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-sm border flex items-center justify-center font-display text-lg"
          style={{ borderColor: `${accent}80`, color: accent, background: "rgba(6,11,20,0.6)" }}
        >
          {initials}
        </div>
        <div>
          <div className="font-display text-lg text-white leading-tight">{name}</div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono-aviation mt-0.5" style={{ color: accent }}>
            {role}
          </div>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-300 leading-relaxed">{bio}</p>
    </div>
  );
}

// ============================================================
// Section · Resource Library
// ============================================================
const LIBRARY_ITEMS = [
  {
    id: "moto",
    type: "Live Demo",
    title: "Motorcycle Foyer",
    sub: "System B · Client Node · full 12-section engine",
    href: "/moto",
    external: false,
    accent: "#DC2626",
    icon: Bike,
    art: "moto",
  },
  {
    id: "bentley",
    type: "Live Demo",
    title: "Bentley — Adventure Captain",
    sub: "Campground vertical · Wooster node showreel",
    href: "/bentley",
    external: false,
    accent: "#34D399",
    icon: Sparkles,
    art: "bentley",
  },
  {
    id: "magic-mirror",
    type: "Live Demo",
    title: "Magic Mirror",
    sub: "Instant business preview · Alfred's foyer for any trade",
    href: "/demo",
    external: false,
    accent: "#60A5FA",
    icon: Radio,
    art: "mirror",
  },
  {
    id: "roadmap-ebook",
    type: "Sample Asset",
    title: "The 2026 Local AI Growth Roadmap",
    sub: "E-book · what every Main Street shop must do before Q3",
    href: "/local",
    external: false,
    accent: "#D4AF37",
    icon: BookOpen,
    art: "ebook-gold",
  },
  {
    id: "handshake-ebook",
    type: "Sample Asset",
    title: "The 7-Hour Handshake",
    sub: "E-book · the recruit's onboarding contract",
    href: "/handshake",
    external: false,
    accent: "#F5C842",
    icon: BookOpen,
    art: "ebook-emerald",
  },
  {
    id: "cockpit",
    type: "Insider Access",
    title: "Leader Cockpit",
    sub: "Strike list · Grid transfers · Campaign architect",
    href: LEADER_COCKPIT_URL,
    external: false,
    accent: "#C0C0C7",
    icon: Shield,
    art: "cockpit",
  },
];

function SectionResourceLibrary() {
  return (
    <section
      className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20"
      data-testid="landing-resource-library"
    >
      <div className="flex items-center gap-3 mb-6 justify-center">
        <div className="hud-line w-10" />
        <span className="hud-label text-gold flex items-center gap-2">
          <BookOpen size={12} /> Resource Library
        </span>
        <div className="hud-line w-10" />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-center max-w-3xl mx-auto">
        Field kit — <span className="text-gold italic">poke around</span>.
      </h2>
      <p className="mt-4 text-slate-400 text-base text-center max-w-2xl mx-auto leading-relaxed">
        Every asset in the Grid is one click away. Explore the live demos,
        thumb through the e-books, or step behind the Leader Cockpit.
      </p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {LIBRARY_ITEMS.map((it) => (
          <ResourceCard key={it.id} item={it} />
        ))}
      </div>
    </section>
  );
}

function ResourceCard({ item }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      data-testid={`landing-resource-${item.id}`}
      className="group glass-strong rounded-sm border border-white/10 hover:border-gold/60 overflow-hidden transition-all block min-h-[240px] flex flex-col"
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ backgroundColor: item.accent, opacity: 0.6 }}
      />
      <div
        className="relative h-32 overflow-hidden border-b border-white/10"
        style={{
          background:
            `linear-gradient(135deg, ${item.accent}22 0%, rgba(6,11,20,0.9) 60%)`,
        }}
      >
        <ResourceArt kind={item.art} accent={item.accent} />
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.28em] font-mono-aviation border"
          style={{ borderColor: `${item.accent}80`, color: item.accent, background: "rgba(6,11,20,0.85)" }}
        >
          {item.type}
        </div>
        <div
          className="absolute bottom-2 right-2 w-9 h-9 rounded-sm border flex items-center justify-center"
          style={{ borderColor: `${item.accent}80`, color: item.accent, background: "rgba(6,11,20,0.85)" }}
        >
          <Icon size={16} />
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display text-lg text-white leading-tight group-hover:text-gold transition-colors">
          {item.title}
        </div>
        <p className="mt-2 text-xs text-slate-400 leading-relaxed flex-1">{item.sub}</p>
        <div
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-display group-hover:translate-x-0.5 transition-transform"
          style={{ color: item.accent }}
        >
          Open <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}

function ResourceArt({ kind, accent }) {
  // Small inline SVG "mockup" artwork per resource type.
  if (kind === "moto") {
    return (
      <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full">
        <path d="M0 90 L200 90" stroke={accent} strokeOpacity="0.3" strokeWidth="1" />
        <circle cx="50" cy="75" r="14" fill="none" stroke={accent} strokeWidth="1.5" />
        <circle cx="150" cy="75" r="14" fill="none" stroke={accent} strokeWidth="1.5" />
        <path d="M60 75 L90 55 L130 55 Q145 55 150 65 L 155 75" fill="none" stroke={accent} strokeWidth="2" />
        <path d="M92 55 Q100 40 130 40 Q142 40 148 52 Q140 56 128 55 L100 55 Z" fill={accent} fillOpacity="0.5" />
      </svg>
    );
  }
  if (kind === "bentley") {
    return (
      <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full">
        {/* Mountain silhouette */}
        <path d="M0 90 L40 55 L70 70 L110 35 L145 60 L200 40 L200 100 L0 100 Z" fill={accent} fillOpacity="0.35" />
        <path d="M0 90 L40 55 L70 70 L110 35 L145 60 L200 40" stroke={accent} strokeWidth="1.5" fill="none" />
        {/* Sun */}
        <circle cx="150" cy="30" r="8" fill={accent} />
      </svg>
    );
  }
  if (kind === "mirror") {
    return (
      <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full">
        {/* Mirror frame */}
        <rect x="45" y="15" width="110" height="70" rx="4" fill="none" stroke={accent} strokeWidth="1.5" />
        <rect x="55" y="25" width="90" height="50" rx="2" fill={accent} fillOpacity="0.15" />
        {[0,1,2,3].map((i) => (
          <line key={i} x1={65} y1={35 + i*10} x2={135} y2={35 + i*10} stroke={accent} strokeOpacity="0.55" strokeWidth="1" />
        ))}
      </svg>
    );
  }
  if (kind === "ebook-gold" || kind === "ebook-emerald") {
    const c2 = kind === "ebook-emerald" ? "#34D399" : accent;
    return (
      <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full">
        {/* Book spine + pages */}
        <rect x="60" y="15" width="80" height="70" rx="2" fill={c2} fillOpacity="0.35" stroke={c2} strokeWidth="1.5" />
        <rect x="63" y="18" width="74" height="64" rx="1" fill="rgba(6,11,20,0.7)" stroke={c2} strokeOpacity="0.4" strokeWidth="0.8" />
        <line x1="70" y1="34" x2="130" y2="34" stroke={c2} strokeWidth="1" />
        <line x1="70" y1="44" x2="120" y2="44" stroke={c2} strokeOpacity="0.6" strokeWidth="0.6" />
        <line x1="70" y1="52" x2="125" y2="52" stroke={c2} strokeOpacity="0.6" strokeWidth="0.6" />
        <line x1="70" y1="60" x2="115" y2="60" stroke={c2} strokeOpacity="0.6" strokeWidth="0.6" />
        <line x1="70" y1="68" x2="122" y2="68" stroke={c2} strokeOpacity="0.6" strokeWidth="0.6" />
      </svg>
    );
  }
  if (kind === "cockpit") {
    return (
      <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full">
        <rect x="20" y="30" width="160" height="55" rx="2" fill="none" stroke={accent} strokeWidth="1" />
        <rect x="30" y="40" width="45" height="12" fill={accent} fillOpacity="0.4" />
        <rect x="30" y="55" width="70" height="6" fill={accent} fillOpacity="0.25" />
        <rect x="30" y="64" width="55" height="6" fill={accent} fillOpacity="0.25" />
        <rect x="110" y="40" width="60" height="35" fill="none" stroke={accent} strokeOpacity="0.5" strokeWidth="0.8" />
        <polyline points="115,70 125,60 135,65 145,50 155,58 165,45" fill="none" stroke={accent} strokeWidth="1.5" />
      </svg>
    );
  }
  return null;
}